import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  motifDoctolibCatalogueListSchema,
  specialiteDoctolibListSchema,
  synchronisationDoctolibListSchema,
  type MotifDoctolibCatalogueList,
  type SpecialiteDoctolibList,
  type SynchronisationDoctolibList,
} from '../schemas';
import { useToken } from './use-token';

export type DoctolibCatalogueListParams = {
  page?: number;
  limit?: number;
  search?: string;
  /** Motifs seulement : restreint à ceux vus chez un cabinet de cette spécialité. */
  specialiteId?: number;
};

/** Qui a synchronisé quel compte Doctolib, et quand — un membre par ligne. */
export function useAdminDoctolibSynchronisations({
  page = 1,
  limit = 50,
  search = '',
}: DoctolibCatalogueListParams = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) query.set('search', search);

  return useQuery({
    queryKey: queryKeys.adminDoctolib.synchronisations({ page, limit, search }),
    queryFn: () =>
      api.get<SynchronisationDoctolibList>(`/admin/doctolib/synchronisations?${query}`, {
        schema: synchronisationDoctolibListSchema,
      }),
    enabled: token !== null,
    placeholderData: keepPreviousData,
  });
}

/**
 * Catalogue des spécialités Doctolib jamais vues, tous cabinets confondus.
 * Alimenté à chaque synchronisation, jamais purgé (voir
 * `UpsertCatalogueDoctolibUseCase` côté backend) : réservé au back-office.
 */
export function useAdminDoctolibSpecialites({
  page = 1,
  limit = 50,
  search = '',
}: DoctolibCatalogueListParams = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) query.set('search', search);

  return useQuery({
    queryKey: queryKeys.adminDoctolib.specialites({ page, limit, search }),
    queryFn: () =>
      api.get<SpecialiteDoctolibList>(`/admin/doctolib/specialites?${query}`, {
        schema: specialiteDoctolibListSchema,
      }),
    enabled: token !== null,
    // Garde la page précédente affichée pendant le chargement de la suivante.
    placeholderData: keepPreviousData,
  });
}

/** Catalogue des motifs de consultation Doctolib jamais vus. Même principe. */
export function useAdminDoctolibMotifs({
  page = 1,
  limit = 50,
  search = '',
  specialiteId,
}: DoctolibCatalogueListParams = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) query.set('search', search);
  if (specialiteId !== undefined) query.set('specialiteId', String(specialiteId));

  return useQuery({
    queryKey: queryKeys.adminDoctolib.motifs({ page, limit, search, specialiteId }),
    queryFn: () =>
      api.get<MotifDoctolibCatalogueList>(`/admin/doctolib/motifs?${query}`, {
        schema: motifDoctolibCatalogueListSchema,
      }),
    enabled: token !== null,
    placeholderData: keepPreviousData,
  });
}
